import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Win10AdminLayout from "../layouts/Win10AdminLayout";
import api from "../services/api";

const STATUS_OPTIONS = [
  { value: "concept", label: "Concept" },
  { value: "pilot", label: "Pilot" },
  { value: "actief", label: "Actief" },
];

const EDITION_OPTIONS = [
  { value: "basic", label: "Basic" },
  { value: "pro", label: "Pro" },
  { value: "enterprise", label: "Enterprise" },
];

const BRANCHE_OPTIONS = [
  { value: "fire_safety", label: "Fire safety" },
  { value: "installation", label: "Installation" },
  { value: "sailmaker", label: "Sailmaker" },
  { value: "signage", label: "Signage" },
  { value: "general_service", label: "General service" },
];

const INITIAL_FORM = {
  bedrijfsnaam: "",
  status: "concept",
  edition: "basic",
  brancheProfile: "general_service",
  contactpersoon: "",
  email: "",
  telefoon: "",
  adres: "",
  interneNotitie: "",
  createAdminUser: false,
};

export default function AdminTenantOnboardingPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [createdTenant, setCreatedTenant] = useState(null);

  const brancheLabel = useMemo(
    () => BRANCHE_OPTIONS.find((item) => item.value === form.brancheProfile)?.label || "-",
    [form.brancheProfile]
  );

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError("");
    setSuccess("");
    setCreatedTenant(null);

    const bedrijfsnaam = String(form.bedrijfsnaam || "").trim();
    if (!bedrijfsnaam) {
      setError("Bedrijfsnaam is verplicht.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        bedrijfsnaam,
        status: form.status,
        edition: form.edition,
        brancheProfile: form.brancheProfile,
        createAdminUser: false,
      };
      const contactpersoon = String(form.contactpersoon || "").trim();
      const email = String(form.email || "").trim();
      const telefoon = String(form.telefoon || "").trim();
      const adres = String(form.adres || "").trim();
      const interneNotitie = String(form.interneNotitie || "").trim();

      if (contactpersoon) payload.contactpersoon = contactpersoon;
      if (email) payload.email = email;
      if (telefoon) payload.telefoon = telefoon;
      if (adres) payload.adres = adres;
      if (interneNotitie) payload.interneNotitie = interneNotitie;

      const res = await api.post("/admin/tenants", payload);
      const tenant = res?.data?.tenant || null;
      const tenantId = String(tenant?._id || "").trim();
      const companyName = String(tenant?.companyName || bedrijfsnaam).trim();

      if (tenantId) {
        navigate(`/admin/tenants/${tenantId}/first-admin`, {
          replace: true,
          state: {
            companyName,
            from: "tenant_onboarding",
          },
        });
        return;
      }

      setCreatedTenant({
        tenantId,
        companyName,
      });
      setSuccess("Tenant onboarding opgeslagen.");
      setForm(INITIAL_FORM);
    } catch (err) {
      const message = err?.response?.data?.message || "Tenant onboarding opslaan mislukt.";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Win10AdminLayout>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Nieuwe Tenant Onboarding</h1>
          <p className="text-sm text-slate-600">
            PlatformAdmin flow: tenant aanmaken + standaardinstellingen/presets klaarzetten.
          </p>
        </div>
        <Link
          to="/admin/tenants"
          className="rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
        >
          Terug naar tenants
        </Link>
      </div>

      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 gap-4 rounded border border-slate-300 bg-white p-5 shadow-sm md:grid-cols-2"
      >
        <Field label="Bedrijfsnaam">
          <input
            type="text"
            value={form.bedrijfsnaam}
            onChange={(e) => updateField("bedrijfsnaam", e.target.value)}
            className="w-full rounded border border-slate-300 px-3 py-2"
            required
          />
        </Field>

        <Field label="Status">
          <select
            value={form.status}
            onChange={(e) => updateField("status", e.target.value)}
            className="w-full rounded border border-slate-300 px-3 py-2"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Pakket / edition">
          <select
            value={form.edition}
            onChange={(e) => updateField("edition", e.target.value)}
            className="w-full rounded border border-slate-300 px-3 py-2"
          >
            {EDITION_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Brancheprofiel">
          <select
            value={form.brancheProfile}
            onChange={(e) => updateField("brancheProfile", e.target.value)}
            className="w-full rounded border border-slate-300 px-3 py-2"
          >
            {BRANCHE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Contactpersoon">
          <input
            type="text"
            value={form.contactpersoon}
            onChange={(e) => updateField("contactpersoon", e.target.value)}
            className="w-full rounded border border-slate-300 px-3 py-2"
          />
        </Field>

        <Field label="E-mail">
          <input
            type="email"
            value={form.email}
            onChange={(e) => updateField("email", e.target.value)}
            className="w-full rounded border border-slate-300 px-3 py-2"
          />
        </Field>

        <Field label="Telefoon">
          <input
            type="text"
            value={form.telefoon}
            onChange={(e) => updateField("telefoon", e.target.value)}
            className="w-full rounded border border-slate-300 px-3 py-2"
          />
        </Field>

        <Field label="Adres">
          <input
            type="text"
            value={form.adres}
            onChange={(e) => updateField("adres", e.target.value)}
            className="w-full rounded border border-slate-300 px-3 py-2"
          />
        </Field>

        <div className="md:col-span-2">
          <Field label="Interne notitie">
            <textarea
              value={form.interneNotitie}
              onChange={(e) => updateField("interneNotitie", e.target.value)}
              rows={4}
              className="w-full rounded border border-slate-300 px-3 py-2"
            />
          </Field>
        </div>

        <div className="md:col-span-2 flex items-center justify-between">
          <p className="text-xs text-slate-500">
            Profiel: <strong>{brancheLabel}</strong> • Hoofdgebruiker wordt via aparte vervolgstap aangemaakt.
          </p>
          <button
            type="submit"
            disabled={submitting}
            className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {submitting ? "Opslaan..." : "Tenant onboarding opslaan"}
          </button>
        </div>
      </form>

      {error ? <p className="mt-3 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</p> : null}
      {success ? <p className="mt-3 rounded border border-green-300 bg-green-50 p-3 text-sm text-green-700">{success}</p> : null}

      {createdTenant ? (
        <div className="mt-4 rounded border border-slate-300 bg-white p-4">
          <h2 className="mb-2 text-base font-semibold text-slate-900">Aangemaakte tenant</h2>
          <p className="text-sm text-slate-700">Bedrijf: {createdTenant.companyName}</p>
          <p className="text-sm text-slate-700">Tenant ID: {createdTenant.tenantId || "-"}</p>
        </div>
      ) : null}
    </Win10AdminLayout>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-slate-700">{label}</span>
      {children}
    </label>
  );
}
